import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/tabs';
import { getCarrierName } from '@/lib/models/carrier/utils/get-carrier-name';
import { LineModel } from '@/lib/models/lines/types';
import { getServiceLineNames } from '@/lib/models/lines/utils/get-line-service-ids';
import { LocationModel } from '@/lib/models/location/types';
import { getCarriersByIdsState } from '@/state/carriers';
import { PortSequence } from '@/types';
import { RouteLinkVehicleApiV4Interface } from '@/types/api-types';
import { joinByCommaAnd } from '@/utils/helpers';
import { tv } from '@/utils/styles';

import { LineCarriers } from '../carriers';
import { LineTimeline } from '../timeline';
import { LineVehicles } from '../vehicles';

export interface LineDetailsProps {
  line: LineModel;
  lineLocations: LocationModel[];
  portSequence: PortSequence[];
  vehicles?: RouteLinkVehicleApiV4Interface[]; // @TODO add model and transformer?
}

const LineDetails = ({ line, lineLocations, portSequence, vehicles }: LineDetailsProps) => {
  const serviceNames = getServiceLineNames(line);
  const operatingCarriers = useRecoilValue(getCarriersByIdsState(line.operatingCarrierIds));
  const operatingCarrierNames = operatingCarriers.map(getCarrierName);

  const { lineName, lineHeader, operatedByText, tabsListWrapper } = styles();

  return (
    <TabsRoot defaultValue="tab-ports" className="flex h-full flex-col">
      <div className={lineHeader()}>
        <Heading variant="h4" className={lineName()}>
          {serviceNames}
        </Heading>
        <Text className={operatedByText()}>Operated by {joinByCommaAnd(operatingCarrierNames)}</Text>
      </div>
      <div className={tabsListWrapper()}>
        <TabsList>
          <TabsTrigger key="ports" value="tab-ports">
            Port calls
          </TabsTrigger>
          <TabsTrigger key="vehicles" value="tab-carriers">
            Carriers / Services
          </TabsTrigger>
          <TabsTrigger key="carriers" value="tab-vehicles">
            Vessel list
          </TabsTrigger>
        </TabsList>
      </div>
      <TabsContent key="ports" value="tab-ports" className="flex-1 overflow-hidden">
        <LineTimeline lineLocations={lineLocations} portSequence={portSequence} />
      </TabsContent>
      <TabsContent key="vehicles" value="tab-vehicles" className="flex-1 overflow-hidden">
        <LineVehicles vehicles={vehicles} />
      </TabsContent>
      <TabsContent key="carriers" value="tab-carriers" className="flex-1 overflow-hidden">
        <LineCarriers line={line} />
      </TabsContent>
    </TabsRoot>
  );
};

const styles = tv({
  slots: {
    lineHeader: 'mb-2 mt-3',
    lineName: 'm-0 font-semibold italic',
    operatedByText: 'm-0 text-sm italic text-text-secondary',
    tabsListWrapper: 'mb-6',
  },
});

export { LineDetails };
